import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Calendar,
  Repeat,
  Users,
  BarChart3,
  Settings,
  Moon,
  Sun,
  Menu,
  X,
} from 'lucide-react';
import { useThemeStore } from '../store/themeStore';
import { useAuth } from '../lib/auth';
import { Logo } from './ui/Logo';
import { FEATURE_FLAGS } from '../config/featureFlags';

const allNavItems = [
  { icon: LayoutGrid, label: 'Dashboard', href: '/dashboard', flag: true as const },
  { icon: Calendar, label: 'Calendar', href: '/calendar', flag: true as const },
  { icon: Repeat, label: 'Content', href: '/content', flag: 'content' as const },
  { icon: Users, label: 'Team', href: '/team', flag: true as const },
  { icon: BarChart3, label: 'Analytics', href: '/analytics', flag: true as const },
  { icon: Settings, label: 'Settings', href: '/settings', flag: true as const },
];

const navItems = allNavItems.filter((item) =>
  item.flag === true || (typeof item.flag === 'string' && FEATURE_FLAGS[item.flag])
);

export const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isDarkMode = useThemeStore((state) => state.isDarkMode);
  const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 lg:hidden">
        <div className={`flex items-center justify-between h-16 px-4 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-b`}>
          <div className="flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg ${
                isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="ml-4 cursor-pointer" onClick={() => handleNavigation('/dashboard')}>
              <Logo />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <div className="absolute inset-0 bg-gray-600 opacity-75" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className={`absolute inset-y-0 left-0 w-64 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-xl`}>
            <div className="h-full flex flex-col">
              <div className="flex items-center h-16 px-6">
                <Logo />
              </div>
              <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavigation(item.href)}
                    className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg ${
                      location.pathname === item.href
                        ? 'bg-gradient-to-r from-[#4b39ef] to-[#39d2c0] text-white'
                        : `${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.label}
                  </button>
                ))}
              </nav>
              <div className="p-4">
                <button
                  onClick={toggleDarkMode}
                  className={`flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-lg ${
                    isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {isDarkMode ? (
                    <Sun className="w-4 h-4 mr-2" />
                  ) : (
                    <Moon className="w-4 h-4 mr-2" />
                  )}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64">
        <div className={`flex flex-col h-full ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-r`}>
          <div className="flex items-center h-16 px-6 cursor-pointer" onClick={() => handleNavigation('/dashboard')}>
            <Logo />
          </div>

          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.href)}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg ${
                  location.pathname === item.href
                    ? 'bg-gradient-to-r from-[#4b39ef] to-[#39d2c0] text-white'
                    : `${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-4">
            <button
              onClick={toggleDarkMode}
              className={`flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-lg ${
                isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-4 h-4 mr-2" />
              ) : (
                <Moon className="w-4 h-4 mr-2" />
              )}
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};